import { useSSEStore } from '../store/sseStore';

/**
 * SSEConnection service - manages Server-Sent Events subscriptions
 *
 * Usage:
 * ```typescript
 * const unsubscribe = sseConnection.subscribe('offer_created', (data) => {
 *   console.log('New offer:', data);
 * });
 *
 * // Cleanup
 * unsubscribe();
 * ```
 */
class SSEConnection {
  private eventHandlers = new Map<string, Set<(data: any) => void>>();
  private registeredEvents = new Set<string>();

  /**
   * Subscribe to an SSE event type
   * @param eventType - The event type to subscribe to
   * @param handler - Callback function to handle the event
   * @returns Unsubscribe function
   */
  subscribe(eventType: string, handler: (data: any) => void): () => void {
    // Initialize handler set for this event type if not exists
    if (!this.eventHandlers.has(eventType)) {
      this.eventHandlers.set(eventType, new Set());
    }

    // Add handler to set
    this.eventHandlers.get(eventType)!.add(handler);

    // Register event listener with EventSource if not already registered
    if (!this.registeredEvents.has(eventType)) {
      this.registerEventListener(eventType);
      this.registeredEvents.add(eventType);
    }

    // Return unsubscribe function
    return () => {
      this.unsubscribe(eventType, handler);
    };
  }

  /**
   * Unsubscribe from an event
   */
  private unsubscribe(eventType: string, handler: (data: any) => void): void {
    const handlers = this.eventHandlers.get(eventType);
    if (handlers) {
      handlers.delete(handler);

      // Remove event type if no more handlers
      if (handlers.size === 0) {
        this.eventHandlers.delete(eventType);
        this.registeredEvents.delete(eventType);
        // Note: We don't remove the EventSource listener as it's not easily removable
        // The handler set check will prevent callbacks from being fired
      }
    }
  }

  /**
   * Register an event listener with the EventSource connection
   */
  private registerEventListener(eventType: string): void {
    const connection = useSSEStore.getState().connection;

    if (!connection) {
      console.warn(`[SSE] Cannot register listener for "${eventType}" - no active connection`);
      return;
    }

    connection.addEventListener(eventType, (event: MessageEvent) => {
      try {
        const data = JSON.parse(event.data);

        // Store last event in state
        useSSEStore.getState().handleEvent({
          type: eventType,
          data,
          timestamp: Date.now(),
        });

        // Call all registered handlers for this event type
        const handlers = this.eventHandlers.get(eventType);
        if (handlers && handlers.size > 0) {
          handlers.forEach((handler) => {
            try {
              handler(data);
            } catch (error) {
              console.error(`[SSE] Error in handler for "${eventType}":`, error);
            }
          });
        }
      } catch (error) {
        console.error(`[SSE] Error parsing event data for "${eventType}":`, error);
      }
    });

    console.log(`[SSE] Registered listener for event type: ${eventType}`);
  }

  /**
   * Re-register all event listeners (useful after reconnection)
   */
  reregisterAllListeners(): void {
    const eventTypes = Array.from(this.registeredEvents);
    this.registeredEvents.clear();

    eventTypes.forEach((eventType) => {
      this.registerEventListener(eventType);
      this.registeredEvents.add(eventType);
    });

    if (eventTypes.length > 0) {
      console.log(`[SSE] Re-registered ${eventTypes.length} event listeners`);
    }
  }

  /**
   * Get list of subscribed event types
   */
  getSubscribedEvents(): string[] {
    return Array.from(this.eventHandlers.keys());
  }

  /**
   * Get connection status
   */
  getStatus(): string {
    return useSSEStore.getState().status;
  }

  /**
   * Get connection error (if any)
   */
  getError(): string | null {
    return useSSEStore.getState().error;
  }

  /**
   * Manually trigger connection
   * @param tokenId - NFT token ID to connect to
   */
  connect(tokenId: string): void {
    useSSEStore.getState().connect(tokenId);
  }

  /**
   * Manually disconnect
   */
  disconnect(): void {
    useSSEStore.getState().disconnect();
  }
}

// Export singleton instance
export const sseConnection = new SSEConnection();

// Export class for testing
export { SSEConnection };
